/* ==========================================================================================
   EcoVessel 2026 · ev-trimax.js · motion of the TriMax hours badge (snippets/ev-trimax.liquid; looks ev-trimax.css)
   Deferred, no dependencies, safe to load twice (the second copy does nothing). Rides on the theme's reveal
   (ev-main.js): when the badge starts to rise (ev:reveal), its digits roll once and every figure lands at the same
   moment (1.5 s), then .is-landed lets a ring breathe out of each disc.

   The roll (Tommy 02-10 judges: "no frame ever shows more hours than the product actually claims"):
   - a leading digit rolls from 0 up to its own value; the last digit of a figure with 2+ digits takes one full turn
     first (0 ... 9, 0 ... its value); a one-digit figure just rolls up to its value;
   - every column eases out (quint out: fast, then a firm landing without expo's long creeping tail, which left the
     digits a third of a line off for ~0.7 s on the first push);
   - the leading digits start a little late (s), so they reach their own values only after the last digit has come
     round: 14 never reads 19 on the way, 150 never reads 159. tools/trimax_snippet.py checks the same maths.
   The real digits stay in the page the whole time (see-through while rolling; ev-trimax.css draws the strip), so
   screen readers, search engines and print always get the figure.

   A mouse over a figure (or over TriMax®) replays its icon's move once: .is-wiggle for 950 ms, never while the
   entrance plays. On the first screen (.ev-in--now), with reduced motion or without JavaScript: the finished
   badge, still.
   ========================================================================================== */
(() => {
  'use strict';
  if (window.EVtmx) return;
  const D = document, W = window;
  const reduce = () => W.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const ease = (x) => (x >= 1 ? 1 : 1 - Math.pow(1 - x, 5));        // quint out
  const inv = (y) => 1 - Math.pow(1 - y, 1 / 5);                     // when ease() reaches y
  const LAND = 1500;                                                 // ms after ev:reveal: every figure lands here
  const START_COLD = 80, START_HOT = 160;                            // the hot figure starts a beat later

  // One figure's roll: the end position of each column (in lines of the strip) and the leading columns' late start.
  const plan = (num, start) => {
    const ds = Array.prototype.slice.call(num.querySelectorAll('.ev-tmx__d'));
    const vals = ds.map((d) => parseInt(d.textContent, 10) || 0);
    const k = vals.length;
    const ends = vals.map((v, i) => (k > 1 && i === k - 1 ? 10 + v : v));
    let s = 0;
    if (k > 1) {
      const top = Math.max.apply(null, vals.slice(0, -1));            // the leading digit that arrives last
      if (top > 0) {
        const xw = inv(9.5 / (10 + vals[k - 1]));                     // the last digit has come round
        const uf = inv(1 - 0.5 / top);                                // that leading digit shows its value
        s = Math.max(0, (xw - uf) / (1 - uf)) + 0.04;
      }
    }
    return { ds, ends, s, k, start, dur: LAND - start };
  };

  const roll = (card) => {
    if (card.evTmx) return;
    card.evTmx = true;
    const plans = Array.prototype.slice.call(card.querySelectorAll('.ev-tmx__num')).map((num) => {
      const hot = !!num.closest('.ev-tmx__stat--hot');
      return plan(num, hot ? START_HOT : START_COLD);
    });
    const land = () => {
      plans.forEach((p) => p.ds.forEach((d) => d.style.removeProperty('--p')));
      card.classList.remove('is-rolling');
      card.classList.add('is-landed');
    };
    if (!plans.length) { land(); return; }
    plans.forEach((p) => p.ds.forEach((d) => d.style.setProperty('--p', '0')));
    card.classList.add('is-rolling');
    const t0 = performance.now();
    const frame = (now) => {
      const t = now - t0;
      plans.forEach((p) => {
        const x = Math.min(1, Math.max(0, (t - p.start) / p.dur));
        p.ds.forEach((d, i) => {
          const u = i === p.k - 1 ? x : Math.min(1, Math.max(0, (x - p.s) / (1 - p.s)));
          d.style.setProperty('--p', (p.ends[i] * ease(u)).toFixed(3));
        });
      });
      if (t < LAND && card.isConnected) W.requestAnimationFrame(frame); else land();
    };
    W.requestAnimationFrame(frame);
  };

  // the badge starts to rise (ev-main.js reveal): roll. A badge shown at once (first screen) never fires this.
  D.addEventListener('ev:reveal', (e) => {
    const card = e.target;
    if (!card.classList || !card.classList.contains('ev-tmx__card') || reduce()) return;
    roll(card);
  });
  // a badge already rising before this file ran (it loads after ev-main.js, so this is only a safety net)
  const late = () => D.querySelectorAll('.ev-tmx__card.ev-in:not(.ev-in--now)').forEach((c) => { if (!reduce()) roll(c); });
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', late); else late();

  // a mouse over a figure or over TriMax®: its icon's move, once, never cut short, never during the entrance
  D.addEventListener('pointerover', (e) => {
    if (e.pointerType !== 'mouse' || reduce() || !e.target.closest) return;
    const part = e.target.closest('.ev-tmx__stat, .ev-tmx__band');
    if (!part || (e.relatedTarget && part.contains(e.relatedTarget))) return;
    const card = part.closest('.ev-tmx__card');
    if (!card || card.classList.contains('ev-armed') || card.classList.contains('is-rolling') || part.classList.contains('is-wiggle')) return;
    if (card.evTmx && !card.classList.contains('is-landed')) return;
    part.classList.add('is-wiggle');
    W.setTimeout(() => part.classList.remove('is-wiggle'), 950);
  }, { passive: true });

  W.EVtmx = { roll };
})();
